import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { getOne, getPage, useConsoleMutation, usePageQuery, type PageParams } from './core'
import { ck } from './keys'
import { arr, bool, iso, num, obj, str, strOrNull } from './mappers'

export type GateQrKind = 'BAG' | 'SEAL'

export interface QrVillagePreview {
  code: string
  nextSequence: number
  isNew: boolean
}

export interface QrCodeRecord {
  id: string
  sequence: number
  payload: string
}

export interface QrBatch {
  id: string
  kind: GateQrKind
  count: number
  startSequence: number
  endSequence: number
  createdAt: string | null
  villageId: string | null
  villageName: string
  villageCode: string
  stateName: string
  stateCode: string
  codes: QrCodeRecord[]
}

export const mapQrBatch = (r: Record<string, unknown>): QrBatch => {
  const village = obj(r.qrVillage ?? r.qr_village)
  const group = obj(village.group)
  const state = obj(village.state)
  return {
    id: str(r.id),
    kind: str(r.kind, 'BAG') as GateQrKind,
    count: num(r.count),
    startSequence: num(r.startSequence ?? r.start_sequence),
    endSequence: num(r.endSequence ?? r.end_sequence),
    createdAt: iso(r.createdAt ?? r.created_at),
    villageId: strOrNull(group.id),
    villageName: str(group.name, 'Unknown village'),
    villageCode: str(village.code),
    stateName: str(state.name),
    stateCode: str(state.code),
    codes: arr(r.codes).map((c) => ({
      id: str(c.id),
      sequence: num(c.sequence),
      payload: str(c.payload),
    })),
  }
}

export function useQrVillagePreview(groupId: string) {
  return useQuery({
    queryKey: [...ck.qrGeneration, 'preview', groupId],
    enabled: Boolean(groupId),
    queryFn: () =>
      getOne(`/qr-generation/villages/${groupId}/preview`, (r): QrVillagePreview => ({
        code: str(r.code),
        nextSequence: num(r.nextSequence ?? r.next_sequence),
        isNew: bool(r.isNew ?? r.is_new),
      })),
  })
}

export interface QrBatchFilters extends PageParams {
  groupId?: string
}

export function useQrBatches(filters: QrBatchFilters) {
  return usePageQuery([...ck.qrGeneration, 'batches', filters], () =>
    getPage('/qr-generation/batches', filters, mapQrBatch),
  )
}

export function fetchQrBatch(id: string): Promise<QrBatch> {
  return getOne(`/qr-generation/batches/${id}`, mapQrBatch)
}

export function useGenerateQrBatch() {
  return useConsoleMutation<{ groupId: string; count: number; kind: GateQrKind }, QrBatch>({
    mutationFn: async (body) => mapQrBatch(obj(await api.post('/qr-generation/batches', body))),
    invalidate: [ck.qrGeneration, ck.rolls],
    success: (batch) =>
      `${batch.count} ${batch.kind === 'SEAL' ? 'seal' : 'bag-tag'} codes generated`,
    errorTitle: 'QR batch not generated',
  })
}
